import React, { useState, useReducer, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort
} from "@syncfusion/ej2-react-grids";

// Components
import Spinner from "../Components/Spinner";
import DeleteModal from "../Components/Modals/Delete-modal";
import ToastError from "../Components/ToastError";
import { toDateISOString } from "../../Services/string.service";

// Services
import { modelsService } from "../../Services/models.service";
import { manufacturerService } from "../../Services/manufacturer.service";
import { categoryService } from "../../Services/category.service";
import { paginationService } from "../../Services/pagination.service";

// Icons
import { SiAddthis } from "react-icons/si";
import { BiEditAlt, BiTrash } from "react-icons/bi";

// Constants
const _pageTitle = "Cadastro de Modelos";
const _listTitle = "Lista de Modelos";
const _registerTitle = "Dado do Modelo";

const formInitialValues = {
  id: 0,
  name: "",
  infoCar: "N",
  active: "S",
  automakerId: 0,
  vehicleCategoryId: 0,
  Created: "",
};

const formReducer = (state, event) => {
  if (event.reset) {
    return formInitialValues;
  }
  return {
    ...state,
    [event.name]: event.value,
  };
};

export default function Models() {
  const [formData, setFormData] = useReducer(formReducer, {});
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState({ show: false, itemName: "" });
  const [editingItem, setEditingItem] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [listManufacturers, setListManufacturers] = useState([]);
  const [listCategories, setListCategories] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedRowData, setSelectedRowData] = useState({});
  const [modelsData, setModelsData] = useState([]);
  const [pageSize, setPageSize] = useState(15);

  const gridAnchorRef = useRef(null);
  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;
  const BOTTOM_MARGIN = 30;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const dispatch = useDispatch();
  dispatch({ type: "title", value: _pageTitle });

  const handleCloseModal = () => {
    setIsDeleting({ show: false, itemName: "" });
  };

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };



  const handleChangeFilter = async (event) => {
    const selectedValue = event.target.value;
    setFilter(selectedValue);
    setIsLoading(true);
    
    try {
      let models;
      if (selectedValue && selectedValue !== "") {
        models = await modelsService.findByAutomaker(selectedValue);
      } else {
        models = await modelsService.list();
      }
      setModelsData(models || []);
    } catch (error) {
      console.error(error);
      setModelsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const onEdit = (item) => {
    setSelectedRowData(item);
    setEditingItem(item);
    setFormData({
      name: "id",
      value: item.vehicleModelId,
    });

    setFormData({
      name: "name",
      value: item.description,
    });

    setFormData({
      name: "automakerId",
      value: item.automakerId,
    });

    setFormData({
      name: "vehicleCategoryId",
      value: item.categoryId,
    });

    setFormData({
      name: "infoCar",
      value: item.infoCar,
    });

    setShowForm(true);
  };

  const onNew = () => {
    setFormData({
      reset: true,
    });
    setShowForm(true);
    setEditingItem({});
    setSelectedRowData({});
  };

  const onCancel = () => {
    setFormData({
      reset: true,
    });
    setShowForm(false);
    setEditingItem({});
    setSelectedRowData({});
  };

  const onCloseToast = () => {
    setShowToast(false);
  };

  const onDelete = (item) => {
    setEditingItem(item);
    setIsDeleting({
      show: true,
      itemName: item.description,
      itemRemoveId: item.vehicleModelId,
    });
  };

  const handleRemove = async (id) => {
    if (id) {
      try {
        setIsLoading(true);

        // Cria payload apenas com os campos que o endpoint espera
        const updatedItem = {
          description: editingItem.description || "",
          automakerName: editingItem.automakerName || "",
          categoryName: editingItem.categoryName || "",
          fipe: "0",
          infoCar: editingItem.infoCar || "N",
          active: "N",
          vehicleModelId: id, // Necessário para o serviço construir a URL
        };

        await modelsService.update(updatedItem);
        // Aplica o filtro atual após deletar
        let updatedModels;
        if (filter && filter !== "") {
          updatedModels = await modelsService.findByAutomaker(filter);
        } else {
          updatedModels = await modelsService.list();
        }
        setModelsData(updatedModels || []);
      } catch (error) {
        console.log(error);
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setShowForm(false);
          setShowToast(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
        }, 1000);
      }
    }
  };

  const onSave = async () => {
    if (formData.name && formData.automakerId && formData.vehicleCategoryId) {
      setIsLoading(true);

      const selectedManufacturer = listManufacturers.find(
        (m) => m.automakerId === Number(formData.automakerId)
      );

      const selectedCategory = listCategories.find(
        (c) => c.categoryId === Number(formData.vehicleCategoryId)
      );

      const payload = {
        description: formData.name,
        infoCar: formData.infoCar || "N",
        fipe: "0",
        active: "S",
        AutomakerName: selectedManufacturer ? selectedManufacturer.description : "",
        CategoryName: selectedCategory ? selectedCategory.description : "",
        Created: toDateISOString(new Date()),
      };

      const payloadUpdate = {
        description: formData.name,
        automakerName: selectedManufacturer ? selectedManufacturer.description : "",
        categoryName: selectedCategory ? selectedCategory.description : "",
        fipe: "0",
        infoCar: formData.infoCar || "N",
        active: "S",
      };

      try {
        setIsLoading(true);

        if (formData.id) payload["id"] = Number(formData.id || 0);

        let response = "";
        // Verifica se está editando (tem vehicleModelId no selectedRowData ou formData.id > 0)
        const isEditing = (selectedRowData.vehicleModelId && selectedRowData.vehicleModelId > 0) || (formData.id && formData.id > 0);
        
        if (isEditing) {
          // Obtém o vehicleModelId para passar para o serviço (já que está na URL, não no body)
          const vehicleModelId = selectedRowData.vehicleModelId || formData.id;
          
          // Cria um objeto com vehicleModelId para o serviço usar na URL
          const updatePayload = {
            ...payloadUpdate,
            vehicleModelId: vehicleModelId
          };
          
          response = await modelsService.update(updatePayload);
        } else {
          response = await modelsService.create(payload);
        }
        
        if (response == null) setShowToast(true);
        if (response) {
          // Aplica o filtro atual após salvar
          let updatedModels;
          if (filter && filter !== "") {
            updatedModels = await modelsService.findByAutomaker(filter);
          } else {
            updatedModels = await modelsService.list();
          }
          setModelsData((updatedModels || []).filter(model => model.active === "S"));
        }

        
      } catch (error) {
        console.log(error);
        setShowToast(true);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setShowForm(false);
          setFormData({
            reset: true,
          });
          setEditingItem({});
          setSelectedRowData({});
        }, 1000);
      }
    }
  };


  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const manufacturers = await manufacturerService.list();
        setListManufacturers(manufacturers);
        setFilter("");
        const models = await modelsService.list();
        setModelsData(models || []);
        setListCategories(await categoryService.list());
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isLoading && modelsData.length > 0) {
      paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT,
        MIN_PAGE,
        MAX_PAGE,
        modelsData,
        window.innerHeight,
        setPageSize
      );
    }

    let t = null;
    const onResize = () => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(() => paginationService.calculateGridMetrics(
        gridAnchorRef,
        BOTTOM_MARGIN,
        HEADER_PAGER_BUDGET,
        ROW_HEIGHT,
        MIN_PAGE,
        MAX_PAGE,
        modelsData,
        window.innerHeight,
        setPageSize
      ), 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (t) window.clearTimeout(t);
    };
  }, [isLoading, modelsData]);


  return (
    <>
      <div className="container-xxl flex-grow-1 container-p-y">
        {isLoading ? <Spinner /> : ""}

        {showToast && (
          <ToastError show={showToast} closeToast={onCloseToast} />
        )}

        <DeleteModal
          itemName={isDeleting.itemName}
          itemRemoveId={isDeleting.itemRemoveId}
          callBackRemove={handleRemove}
          show={isDeleting.show}
          handleClose={handleCloseModal}
        />

        <div
          className="card"
          style={showForm || isLoading ? { display: "none" } : { display: "" }}
        >
          <div className="d-flex justify-content-between">
            <div className="p-2 card-header">
              <h5>{_listTitle}</h5>
            </div>
            <div className="p-2"></div>
            <div className="p-2">
              <SiAddthis className="icon-new" onClick={onNew} />
            </div>
          </div>
          <div className="p-2">
            <div className="col-sm-3">
              <label htmlFor="defaultSelect" className="form-label">
                Filtro por Montadora
              </label>
              <select
                name="manufacturer"
                className="form-control"
                onChange={handleChangeFilter}
                value={filter || ""}
              >
                <option value="">--Montadora--</option>
                {listManufacturers &&
                  listManufacturers.map((item, index) => (
                    <option key={index} value={item.automakerId}>
                      {item.description}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div ref={gridAnchorRef} />
          <GridComponent
            dataSource={modelsData}
            allowPaging
            allowSorting={true}
            allowFiltering={true}
            filterSettings={{ type: "Menu" }}
            pageSettings={{ pageSize }}
            rowHeight={ROW_HEIGHT}
            height={"auto"}
            style={{ marginTop: 16 }}
            locale="pt-BR"
            rowSelected={null}
            rowDataBound={(args) => {
              // Remove cursor pointer já que agora temos botões de ação
            }}
            actionComplete={() => {
              setTimeout(() => paginationService.calculateGridMetrics(
                gridAnchorRef,
                BOTTOM_MARGIN,
                HEADER_PAGER_BUDGET,
                ROW_HEIGHT,
                MIN_PAGE,
                MAX_PAGE,
                modelsData,
                window.innerHeight,
                setPageSize
              ), 50);
            }}
          >
            <ColumnsDirective>
              <ColumnDirective field="vehicleModelId" headerText="Id" width="80" />
              <ColumnDirective field="description" headerText="Descrição" width="200" />
              <ColumnDirective field="automakerName" headerText="Montadora" width="150" />
              <ColumnDirective field="categoryName" headerText="Categoria" width="150" />
              <ColumnDirective field="active" headerText="Ativo" width="80" template={(props) => (
                <span>{props.active || "N"}</span>
              )} />
              <ColumnDirective headerText="Ações" width="80" textAlign="Left" template={(props) => (
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-start', paddingLeft: '8px' }}>
                  <BiEditAlt 
                    className="icon-button"
                    style={{ cursor: 'pointer', fontSize: '18px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(props);
                    }}
                    title="Editar"
                  />
                  <BiTrash 
                    className="icon-button"
                    style={{ cursor: 'pointer', fontSize: '18px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(props);
                    }}
                    title="Deletar"
                  />
                </div>
              )} />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Sort]} />
          </GridComponent>

        </div>

        <div
          className="row py-3"
          style={
            !showForm || isLoading ? { display: "none" } : { display: "block" }
          }
        >
          <div className="col-6">
            <div className="card">
              <h5 className="card-header">{_registerTitle}</h5>
              <div className="card-header">
                {formData.id > 0 ? (
                  <div className="row mb-3">
                    <label
                      className="col-sm-2 col-form-label"
                      htmlFor="basic-default-name"
                    >
                      ID
                    </label>
                    <div className="col-sm-10">
                      <label
                        className="col-sm-2 col-form-label"
                        htmlFor="basic-default-name"
                      >
                        {formData.id}
                      </label>
                    </div>
                  </div>
                ) : (
                  ""
                )}

                <input
                  type="hidden"
                  value={formData.infoCar || ""}
                  id="infoCar"
                  name="infoCar"
                />
                <div className="row mb-3">
                  <label
                    className="col-sm-2 col-form-label"
                    htmlFor="basic-default-name"
                  >
                    Nome
                  </label>
                  <div className="col-sm-10">
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      placeholder="Nome"
                      value={formData.name || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label
                    className="col-sm-2 col-form-label"
                    htmlFor="basic-default-name"
                  >
                    Montadora
                  </label>
                  <div className="col-sm-10">
                    <select
                      name="automakerId"
                      className="form-control"
                      onChange={handleChange}
                      value={formData.automakerId || ""}
                    >
                      <option value="">--Montadora--</option>
                      {listManufacturers &&
                        listManufacturers.map((item, index) => (
                          <option key={index} value={item.automakerId}>
                            {item.description}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <label
                    className="col-sm-2 col-form-label"
                    htmlFor="basic-default-name"
                  >
                    Categoria
                  </label>
                  <div className="col-sm-10">
                    <select
                      name="vehicleCategoryId"
                      className="form-control"
                      onChange={handleChange}
                      value={formData.vehicleCategoryId || ""}
                    >
                      <option value="">--Categoria--</option>
                      {listCategories &&
                        listCategories.map((item, index) => (
                          <option key={index} value={item.categoryId}>
                            {item.description}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div
                  className="row justify-content-end"
                  style={{ textAlign: "end" }}
                >
                  <div className="col-ls-8 col-md-8 col-xs-12 mb-3">
                    <button
                      type="button"
                      className="btn btn-warning"
                      style={{ marginRight: "10px" }}
                      onClick={onCancel}
                    >
                      Cancelar
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSave}
                      disabled={isLoading}
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
